import { McpConnection } from "@models";
import {
  Button,
  Chip,
  ChipProps,
  FormControlLabel,
  Link,
  Switch,
  Typography,
} from "@mui/material";
import { IntegrationConfig } from "@types";
import React from "react";
import { IntegrationCardIcon } from "./IntegrationCardIcon";
import { IntegrationCardShell } from "./IntegrationCardShell";

export interface IntegrationCardConfig
  extends Omit<IntegrationConfig, "id"> {
  id: string;
  documentationUrl?: string;
  documentationLabel?: string;
}

interface IntegrationCardProps {
  config: IntegrationCardConfig;
  agentEnabled?: boolean;
  onToggleAgentEnabled?: (enabled: boolean) => void;
  isTogglingAgentEnabled?: boolean;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onTest?: () => void;
  isConnecting?: boolean;
  isDisconnecting?: boolean;
  isTesting?: boolean;
}

interface McpConnectionCardProps {
  connection: McpConnection;
  faviconUrl?: string | string[] | null;
  onToggleAgentEnabled?: (enabled: boolean) => void;
  isTogglingAgentEnabled?: boolean;
  onDisconnect: () => void;
  onTest: () => void;
  isDisconnecting?: boolean;
  isTesting?: boolean;
}

const getIntegrationStatusColor = (
  status: IntegrationCardConfig["status"]
): ChipProps["color"] => {
  switch (status) {
    case "connected":
      return "success";
    case "disconnected":
      return "default";
    case "coming_soon":
      return "default";
    default:
      return "default";
  }
};

const getIntegrationStatusLabel = (status: IntegrationCardConfig["status"]) => {
  switch (status) {
    case "connected":
      return "Connected";
    case "disconnected":
      return "Not Connected";
    case "coming_soon":
      return "Coming Soon";
    default:
      return "Unknown";
  }
};

export const IntegrationCard: React.FC<IntegrationCardProps> = ({
  config,
  agentEnabled = true,
  onToggleAgentEnabled,
  isTogglingAgentEnabled = false,
  onConnect,
  onDisconnect,
  onTest,
  isConnecting = false,
  isDisconnecting = false,
  isTesting = false,
}) => {
  return (
    <IntegrationCardShell
      title={config.name}
      description={config.description}
      statusLabel={getIntegrationStatusLabel(config.status)}
      statusColor={getIntegrationStatusColor(config.status)}
      icon={
        <IntegrationCardIcon
          icon={config.logo}
          alt={config.name}
          fallbackText={config.name.charAt(0).toUpperCase()}
        />
      }
      badges={
        config.isBeta ? <Chip label="Beta" size="small" color="warning" /> : null
      }
      metadata={
        config.documentationUrl ? (
          <Link
            href={config.documentationUrl}
            target="_blank"
            rel="noreferrer"
            underline="hover"
            variant="body2"
          >
            {config.documentationLabel || "Setup guide"}
          </Link>
        ) : null
      }
      actions={
        <>
          {config.status === "connected" && (
            <>
              {onTest && (
                <Button
                  size="small"
                  variant="text"
                  onClick={onTest}
                  disabled={isTesting}
                >
                  {isTesting ? "Testing..." : "Test"}
                </Button>
              )}
              {onDisconnect && (
                <Button
                  size="small"
                  variant="text"
                  color="error"
                  onClick={onDisconnect}
                  disabled={isDisconnecting}
                >
                  {isDisconnecting ? "Disconnecting..." : "Disconnect"}
                </Button>
              )}
              {onToggleAgentEnabled && (
                <FormControlLabel
                  sx={{ ml: 1, mr: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={agentEnabled}
                      disabled={isTogglingAgentEnabled}
                      onChange={(_, checked) => onToggleAgentEnabled(checked)}
                    />
                  }
                  label="Agent Access"
                />
              )}
            </>
          )}

          {config.status === "disconnected" && onConnect && (
            <Button
              size="small"
              variant="contained"
              onClick={onConnect}
              disabled={isConnecting || !config.isEnabled}
            >
              {isConnecting ? "Connecting..." : "Connect"}
            </Button>
          )}

          {config.status === "coming_soon" && (
            <Button size="small" variant="outlined" disabled>
              Coming Soon
            </Button>
          )}
        </>
      }
    />
  );
};

export const McpConnectionCard: React.FC<McpConnectionCardProps> = ({
  connection,
  faviconUrl,
  onToggleAgentEnabled,
  isTogglingAgentEnabled = false,
  onDisconnect,
  onTest,
  isDisconnecting = false,
  isTesting = false,
}) => {
  return (
    <IntegrationCardShell
      title={connection.name}
      description={connection.serverUrl}
      statusLabel={connection.connected ? "Connected" : "Not Authenticated"}
      statusColor={connection.connected ? "success" : "warning"}
      icon={
        <IntegrationCardIcon
          icon={faviconUrl}
          alt={`${connection.name} icon`}
          hideOnError
        />
      }
      metadata={
        <Typography variant="caption" color="text.secondary">
          Auth mode: {connection.authMode}
        </Typography>
      }
      actions={
        <>
          <Button
            size="small"
            variant="text"
            onClick={onTest}
            disabled={isTesting}
          >
            {isTesting ? "Testing..." : "Test"}
          </Button>
          <Button
            size="small"
            variant="text"
            color="error"
            onClick={onDisconnect}
            disabled={isDisconnecting}
          >
            {isDisconnecting ? "Disconnecting..." : "Disconnect"}
          </Button>
          {connection.connected && onToggleAgentEnabled && (
            <FormControlLabel
              sx={{ ml: 1, mr: 0 }}
              control={
                <Switch
                  size="small"
                  checked={connection.enabled}
                  disabled={isTogglingAgentEnabled}
                  onChange={(_, checked) => onToggleAgentEnabled(checked)}
                />
              }
              label="Agent Access"
            />
          )}
        </>
      }
    />
  );
};
